import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionHead, PageHero } from "@/components/Shared";
import { REG_TYPES, API_URL } from "@/data/content";

export default function Register() {
    const [params] = useSearchParams();
    const [selected, setSelected] = useState("Attendee");
    const [form, setForm] = useState({ name: "", email: "", phone: "", organisation: "", interest: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const t = params.get("type");
        if (t) {
            const match = REG_TYPES.find((r) => r.type.toLowerCase() === t.toLowerCase());
            if (match) setSelected(match.type);
        }
    }, [params]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await axios.post(`${API_URL}/registrations`, { reg_type: selected, ...form });
            toast.success(res.data.message);
            setForm({ name: "", email: "", phone: "", organisation: "", interest: "" });
        } catch {
            toast.error("Registration could not be submitted right now. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const pick = (t) => {
        setSelected(t);
        document.getElementById("register-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div data-testid="register-page">
            <PageHero
                testid="register-hero"
                eyebrow="Registration · 29–31 January 2027"
                title={<>Take Your Place in <span className="gold-text">the Bazaar</span></>}
                sub="Choose how you want to experience Startup MahaBazaar 2027. Registration pricing will be announced soon — register your interest today."
            />

            <section className="py-16 md:py-24">
                <div className="container-x">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {REG_TYPES.map((r, i) => (
                            <Reveal key={r.type} delay={(i % 4) * 60}>
                                <button onClick={() => pick(r.type)} data-testid={`reg-card-${r.type.toLowerCase()}`}
                                    className={`card-luxe p-7 h-full text-left w-full ${selected === r.type ? "!border-[var(--gold)] shadow-[0_0_40px_rgba(201,162,75,0.12)]" : ""}`}>
                                    <div className="card-glow" />
                                    <div className="flex items-center justify-between">
                                        <r.icon size={22} strokeWidth={1.2} className="text-[var(--gold)]" />
                                        <span className="text-[0.55rem] tracking-[0.26em] uppercase text-[var(--champagne)]/70 border hairline px-2.5 py-1">{r.price}</span>
                                    </div>
                                    <h3 className="font-display text-2xl mt-6 text-[var(--ivory)]">{r.type}</h3>
                                    <div className="mt-4 space-y-3 text-xs leading-relaxed">
                                        <p><span className="text-[var(--gold)] tracking-[0.2em] uppercase text-[0.55rem] block mb-1">Who it's for</span><span className="text-muted-luxe">{r.who}</span></p>
                                        <p><span className="text-[var(--gold)] tracking-[0.2em] uppercase text-[0.55rem] block mb-1">What you get</span><span className="text-muted-luxe">{r.gets}</span></p>
                                        <p><span className="text-[var(--gold)] tracking-[0.2em] uppercase text-[0.55rem] block mb-1">Why it matters</span><span className="text-muted-luxe">{r.why}</span></p>
                                    </div>
                                    <span className={`mt-6 inline-flex items-center gap-2 text-[0.6rem] tracking-[0.3em] uppercase ${selected === r.type ? "text-[var(--gold)]" : "text-[var(--champagne)]/80"}`}>
                                        {selected === r.type ? "Selected" : "Register"} →
                                    </span>
                                </button>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-24 bg-[#1C060C]/60" id="register-form">
                <div className="container-x max-w-3xl">
                    <SectionHead eyebrow="Register Your Interest" title={<>You're Registering as <span className="gold-text">{selected}</span></>} />
                    <Reveal className="mt-12">
                        <form onSubmit={submit} className="card-luxe p-8 md:p-10" data-testid="registration-form">
                            <div className="card-glow" />
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="reg-type" className="label-luxe">Participation Type</label>
                                    <select id="reg-type" value={selected} onChange={(e) => setSelected(e.target.value)} className="field" data-testid="reg-type-select">
                                        {REG_TYPES.map((r) => <option key={r.type} value={r.type}>{r.type}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="reg-name" className="label-luxe">Full Name *</label>
                                    <input id="reg-name" required value={form.name} onChange={set("name")} className="field" placeholder="Your name" data-testid="reg-name-input" />
                                </div>
                                <div>
                                    <label htmlFor="reg-email" className="label-luxe">Email *</label>
                                    <input id="reg-email" type="email" required value={form.email} onChange={set("email")} className="field" placeholder="you@example.com" data-testid="reg-email-input" />
                                </div>
                                <div>
                                    <label htmlFor="reg-phone" className="label-luxe">Phone *</label>
                                    <input id="reg-phone" required minLength={6} value={form.phone} onChange={set("phone")} className="field" placeholder="+91 ·····" data-testid="reg-phone-input" />
                                </div>
                                <div>
                                    <label htmlFor="reg-org" className="label-luxe">Organisation</label>
                                    <input id="reg-org" value={form.organisation} onChange={set("organisation")} className="field" placeholder="Startup / company (optional)" data-testid="reg-org-input" />
                                </div>
                                <div>
                                    <label htmlFor="reg-interest" className="label-luxe">I'm Most Interested In</label>
                                    <select id="reg-interest" value={form.interest} onChange={set("interest")} className="field" data-testid="reg-interest-select">
                                        <option value="">Choose…</option>
                                        {["Innovation Bazaar & Pavilions", "State Pavilion / CoE Showcase", "PitchYatra 2027", "Investor Adda", "MahaTalks", "Knowledge Labs", "Exhibiting", "Partnerships", "Awards Night"].map((o) => (
                                            <option key={o} value={o}>{o}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <button type="submit" disabled={busy} className="btn btn-gold mt-8 w-full" data-testid="reg-submit-btn">
                                {busy ? "Submitting…" : `Register as ${selected}`}
                            </button>
                            <p className="mt-5 text-center text-[0.6rem] tracking-[0.24em] uppercase text-muted-luxe">
                                Pricing coming soon — your interest reserves priority access.
                            </p>
                        </form>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
